import React from 'react';
import LegalPage from '@/components/LegalPage';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';

const Privacy = () => (
  <>
    <Seo
      title="Privacy Policy · AdAura Awards 2026"
      description="AdAura Awards privacy policy — how we collect, use and protect personal data of nominators, jurors and visitors. Presented by Marketing Mind."
      path="/privacy"
      jsonLd={[
        webPageSchema({ path: '/privacy', name: 'Privacy Policy', description: 'Privacy Policy of the AdAura Awards 2026.' }),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Privacy Policy', path: '/privacy' }]),
      ]}
    />
  <LegalPage
    kicker="Legal"
    title="Privacy Policy"
    lastUpdated="26 February 2026"
  >
    <p>
      The AdAura Awards 2026 (“the Awards”, “we”, “us”, “our”) is presented by
      Marketing Mind, a media property operated from Noida, India. This Privacy
      Policy explains how we collect, use, store and disclose information when
      you interact with <strong>adauraawards.com</strong>, register as a
      nominator, sit on the jury, or contact our team.
    </p>

    <p>
      By using the site or any AdAura service, you consent to the practices
      described here. If you do not agree, please discontinue use.
    </p>

    <h2>1. Information we collect</h2>
    <ul>
      <li><strong>Account data</strong> — name, organisation, designation, email, mobile number when you register to nominate or sit on the jury.</li>
      <li><strong>Entry data</strong> — campaign details, brand &amp; agency credits, dates, descriptions, files (PDFs, images, video links) uploaded to support a nomination.</li>
      <li><strong>Payment data</strong> — Razorpay transaction identifiers and metadata only. We never receive or store full card / UPI / bank credentials.</li>
      <li><strong>Communication data</strong> — messages submitted through the Contact form, sponsor enquiries, support emails.</li>
      <li><strong>Technical data</strong> — IP address, browser, device, referrer URL, pages visited, captured via standard server logs and reCAPTCHA risk scoring.</li>
    </ul>

    <h2>2. How we use it</h2>
    <ul>
      <li>To process nominations and present them blind to the jury.</li>
      <li>To send transactional notifications — OTP verification, submission acknowledgements, payment receipts, shortlist and winner announcements.</li>
      <li>To collect entry fees and issue GST-compliant tax invoices.</li>
      <li>To prevent fraud and abuse via Google reCAPTCHA v3 and IP-based rate limiting.</li>
      <li>To improve the awards programme — anonymised analytics on category interest, completion rates and submission funnel.</li>
    </ul>

    <h2>3. Legal basis (India — DPDP Act 2023)</h2>
    <p>
      We process personal data under the lawful grounds of <em>consent</em>
      (registration, marketing) and <em>legitimate use</em> (payment processing,
      jury operations, statutory accounting under the GST Act). Sensitive
      personal data is not collected.
    </p>

    <h2>4. Sharing</h2>
    <p>
      We share data with a small set of vetted processors strictly for service
      delivery:
    </p>
    <ul>
      <li><strong>Google Workspace (Gmail SMTP)</strong> — outbound transactional and lifecycle email delivery from <em>awards@marketingmind.co</em>.</li>
      <li><strong>Razorpay</strong> — payment gateway and GST invoice reference.</li>
      <li><strong>Cloudflare R2</strong> — encrypted object storage for entry decks, videos, images and supporting documents.</li>
      <li><strong>MongoDB Atlas</strong> — managed primary database hosted in the AWS Asia region.</li>
      <li><strong>Google reCAPTCHA v3</strong> — bot / spam detection on public forms.</li>
    </ul>
    <p>
      Each processor is bound by their own enterprise data-protection agreements
      and processes data only on our documented instructions. We never sell
      personal data and we do not run third-party advertising trackers.
    </p>

    <h2>5. Retention</h2>
    <ul>
      <li>Entry submissions, scores and accolades — retained indefinitely as part of the AdAura editorial record.</li>
      <li>Tax invoices and payment records — retained 8 years (statutory requirement under the GST Act).</li>
      <li>Account credentials of unsuccessful drafts — purged 18 months after the close of the edition.</li>
      <li>Contact-form messages and sponsor enquiries — retained 24 months.</li>
    </ul>

    <h2>6. Your rights</h2>
    <p>
      You may at any time request access to, correction of, or erasure of your
      personal data (subject to statutory retention obligations). To exercise
      these rights, write to
      <a href="mailto:awards@marketingmind.co"> awards@marketingmind.co</a>{' '}
      with the subject line “DPDP request”. We respond within 30 days.
    </p>

    <h2>7. Cookies &amp; tracking</h2>
    <p>
      We use first-party cookies strictly for session authentication. We do not
      run third-party advertising trackers. reCAPTCHA loads Google’s analytics
      cookies for risk scoring; this is required for spam protection and cannot
      be opted out without disabling form submission.
    </p>

    <h2>8. Security</h2>
    <p>
      Traffic is encrypted in transit (TLS 1.2+). Passwords are stored as
      bcrypt hashes. Backups are encrypted at rest in Cloudflare R2 with daily
      automatic snapshots of the production database. We notify affected users
      within 72 hours of confirming any personal-data breach.
    </p>

    <h2>9. Children</h2>
    <p>
      The Awards are intended for marketing professionals. We do not knowingly
      collect data from anyone under 18.
    </p>

    <h2>10. Updates to this policy</h2>
    <p>
      We may revise this policy from time to time. The “Last updated” date at
      the top reflects the most recent change. Material changes will be
      announced via email to registered nominators and jurors.
    </p>
  </LegalPage>
  </>
);

export default Privacy;
